
import { Component, Watch } from 'vue-property-decorator'
import { dataLayer, ga4Token } from "../core/constants";
import { TIMEOUT_AMOUNT_ROUND_OFF_MESSAGE, PROP_AMOUNT_ID, PROP_MIN_AMOUNT_ID } from '../helpers/constants'
import { findPropWithId, isSalesMultipleRequired, onKeyDown, roundOffAmount } from '../helpers/functions'
import Mixin from '../helpers/mixin'

export enum Steps {
  One,
  Two,
  Three,
  Four
}

@Component
export default class BasicShoppingCart extends Mixin {
      activeStep = 0
      steps: typeof Steps = Steps
      enableStepThree = false
      enabledConfirmation = false
      transition: 'slide-from-right' | 'slide-from-left' = 'slide-from-right'

      inputRoundOffMessage = this.$options?.filters?.t('inputRoundOffMessage')
      showInputMessageOfId: string | null = null
      timeoutId: any = null
      rowLoading: number | null = null
        functionOnKeyDown = onKeyDown
      loaderButton: number | null = null

      singleArticleLabel = this.$options?.filters?.t('singleVariation')
      articleLabel = this.$options?.filters?.t('variations')

      itemCount: null | number = null

      get isStepOne(): boolean {
        return this.activeStep === Steps.One
      }

      get isStepTwo(): boolean {
        return this.activeStep === Steps.Two
      }

      get isStepThree(): boolean {
        return this.activeStep === Steps.Three
      }

      get confirmationStep(): boolean {
        return this.activeStep === Steps.Four
      }

      get stepOneTitle(): string {
        if(this.itemCount === null) return ''
        const label = this.itemCount === 1 ? this.singleArticleLabel : this.articleLabel
        return `${this.itemCount.toString()} ${label}`
      }

      gotToStep(selectedStep: number, cartId: any, cartItems: any): void {
        if (this.activeStep === selectedStep) return
        this.transition = this.activeStep < selectedStep ? 'slide-from-right' : 'slide-from-left'
        this.activeStep = selectedStep
        this.sendGaCheckoutEvent(this.activeStep + 1, cartId, cartItems)
      }

      setRemoveItemLoader(index: number | null): void {
        this.rowLoading = this.rowLoading === index ? null : index
      }

      setButtonLoader(index: number | null): void {
        this.loaderButton = this.loaderButton === index ? null : index
      }


      openRemoveItemPopup(id: string): void {
        this.$store.commit('confirmation/open')
        this.$store.commit('cart/setItemId', id)
      }

      goToUrl(url: string): void {
        if (url) {
          window.location.href = url
        }
      }

      scrollErrorInView(stepCss: string): void {
        const invalidQuerySelector = `.shopping-cart .${stepCss} .error`
        const allInvalidFields = document.querySelectorAll(invalidQuerySelector)
        // get first component by checking the offsetTop (html order is not the same as display order)
        const firstEl = [...(allInvalidFields as any as HTMLElement[])].reduce((prev, current) => {
          return prev.offsetTop < current.offsetTop ? prev : current
        })
        firstEl.scrollIntoView({ block: 'center', inline: 'nearest' })
      }

      isValidAmount(amount: number, salesUnit: number, minAmount: number): boolean {
        if(salesUnit === 0) salesUnit = 1
        return !amount || (!!amount && amount >= minAmount && amount % salesUnit === 0)
      }

      // Cart items may have properties on product or on dynamicProperties, so merge here.
      getItemProperties(cartItem: any): any {
        return {
          properties: cartItem.product?.properties,
          dynamicProperties: cartItem.product?.dynamicProperties ?? cartItem.dynamicProperties
        }
      }

      getCorrectAmount(quantity: number, cartItem: any): number {
        const itemProperties = this.getItemProperties(cartItem)
        // Only round to sales unit when Veelvoudverplichting is enabled.
        if (!isSalesMultipleRequired(itemProperties)) {
          return typeof quantity === 'string' ? +(quantity) : quantity
        }
        const salesUnit: number | string = findPropWithId(itemProperties, PROP_AMOUNT_ID, '1')
        const minAmountRaw: number | string = findPropWithId(itemProperties, PROP_MIN_AMOUNT_ID, '1')
        const salesUnitAsNumber = typeof salesUnit === 'string' ? +((salesUnit as string).replace(',','.')) : salesUnit
        const minAmountAsNumber = typeof minAmountRaw === 'string' ? +((minAmountRaw as string).replace(',','.')) : minAmountRaw
        const customSalesUnit = salesUnitAsNumber <= 0 ? 1 : salesUnitAsNumber
        if (typeof quantity === 'string') quantity = +(quantity)
        const normalizedMinAmount = minAmountAsNumber && minAmountAsNumber > 0 ? minAmountAsNumber : 1

        if (this.isValidAmount(quantity, customSalesUnit, normalizedMinAmount) && quantity !== 0 ) return quantity

        const normalizedQuantity = Math.max(quantity, normalizedMinAmount)
        const amount = roundOffAmount(customSalesUnit, normalizedQuantity)

        quantity = amount

        this.showInputMessageOfId = cartItem.id
        this.handleTimeoutForInputMessage()
        return quantity
      }


      getSalesUnit(cartItem: any): number {
        const itemProperties = this.getItemProperties(cartItem)
        // When multiples are not required, step by 1.
        if (!isSalesMultipleRequired(itemProperties)) {
          return 1
        }
        const salesUnit: number | string = findPropWithId(itemProperties, PROP_AMOUNT_ID, '1')
        const salesUnitAsNumber = typeof salesUnit === 'string' ? +((salesUnit as string).replace(',','.')) : salesUnit
        const roundedSalesUnit = salesUnitAsNumber <= 0 ? 1 : salesUnitAsNumber

        return roundedSalesUnit
      }

      handleTimeoutForInputMessage() {
        // stop timeout when one is in progress
        if (this.timeoutId) {
          clearTimeout(this.timeoutId)
        }

        this.timeoutId = setTimeout(() => {
          this.showInputMessageOfId = null
          this.timeoutId = null
        }, TIMEOUT_AMOUNT_ROUND_OFF_MESSAGE);
      }


      sendGaCheckoutEvent(step: number, cartId: any, cartItems: any[]): void {
        if (!dataLayer) return
        dataLayer.push({ ecommerce: null });  // Clear the previous ecommerce object.
        dataLayer.push({
          event: 'checkout',
          ecommerce: {
            checkout: {
              actionField: {
                step,
                id: cartId
              },
              products: cartItems.map(item => {
                const quantity = typeof item.quantityDecimal === 'string' ? +(item.quantityDecimal.replace(",", ".")) : item.quantityDecimal
                return {
                  name: item.sku,
                  id: item.id,
                  quantity: quantity
                }
              })
            }
          }
        });

        //  GA4 event for checkout does not have steps anymore and the steps need to be split to event for payment info and shipping info instead
        if(ga4Token){
          let eventName = null
          switch(this.activeStep){
          case Steps.One:
            eventName = 'begin_checkout'
            break;
          case Steps.Two:
            eventName = 'add_payment_info'
            break;

          case Steps.Three:
            eventName = 'add_shipping_info'
            break;
          }

          dataLayer.push({ ecommerce: null });  // Clear the previous ecommerce object.
          /* eslint-disable @typescript-eslint/camelcase */
          dataLayer.push({
            event: eventName,
            ecommerce: {
              items: cartItems.map(item => {
                const quantity = typeof item.quantityDecimal === 'string' ? +(item.quantityDecimal.replace(",", ".")) : item.quantityDecimal
                return {
                  item_name: item.sku,
                  item_id: item.id,
                  quantity: quantity
                }
              })

            }
          });
        }
      }

      sendGaPurchaseEvent(cartId: any, cartItems: any[]): void {
        if (!dataLayer) return
        dataLayer.push({ ecommerce: null });  // Clear the previous ecommerce object.
        dataLayer.push({
          event: 'purchase',
          ecommerce: {
            items: cartItems.map(item => {
              const quantity = typeof item.quantityDecimal === 'string' ? +(item.quantityDecimal.replace(",", ".")) : item.quantityDecimal
              return {
                item_name: item.sku,
                item_id: item.id,
                quantity: quantity
              }
            }),
            purchase: {
              actionField: {
                id: cartId
              },
              products: cartItems.map(item => {
                const quantity = typeof item.quantityDecimal === 'string' ? +(item.quantityDecimal.replace(",", ".")) : item.quantityDecimal
                return {
                  name: item.sku,
                  id: item.id,
                  quantity: quantity
                }
              })
            }
          }
        });
      }

      sendGaRemoveFromCartEvent(product: any): void {
        if (!dataLayer) return

        const quantity = typeof product.quantityDecimal === 'string' ? +(product.quantityDecimal.replace(",", ".")) : product.quantityDecimal
        dataLayer.push({ ecommerce: null });  // Clear the previous ecommerce object.
        dataLayer.push({
          event: 'removeFromCart',
          ecommerce: {
            items: [{
              item_name: product.sku,
              item_id: product.id,
              quantity: quantity
            }],
            remove: {
              products: [{
                name: product.sku,
                id: product.id,
                quantity: quantity
              }]
            }
          }
        });
      }

      @Watch('$store.state.cart.itemsCount')
      onItemCountChange(itemCount: null | number) {
        this.itemCount = itemCount
      }

}
